import { ReactElement, useEffect, useMemo, useState } from 'react';
import BigNumber from 'bignumber.js';

import {
  EMPTY_STAKING_CHART_DATA,
  IStakingChartData,
  useGetStakingChartDataQuery,
} from 'modules/analytics/actions/getStakingChartData';
import { StakingChart } from 'modules/analytics/components/StakingChart/StakingChart';
import { RangeSelectorType } from 'modules/api/const';
import { Card } from 'modules/common';
import { useGetStatsQuery } from 'modules/dashboard/actions/getStats';
import { useGlobalTranslation } from 'modules/i18n/hooks/useGlobalTranslation';

import { Info } from '../Info';
import { RangeSelector } from '../RangeSelector';
import { translation } from './translation';
import { useStyles } from './useStyles';

export function TotalStaked(): ReactElement {
  const { t, keys } = useGlobalTranslation(translation);
  const { classes } = useStyles();

  const [range, setRange] = useState(RangeSelectorType.Week);

  const { data } = useGetStatsQuery();
  const totalStaked = useMemo(
    () => (data?.tvl ? new BigNumber(data.tvl) : undefined),
    [data?.tvl],
  );

  const totalStakedIOTA = useMemo(() => {
    const usdPrice = new BigNumber(data?.iotaUsdPrice || 0);

    return totalStaked && !usdPrice.isZero()
      ? totalStaked.div(usdPrice)
      : undefined;
  }, [data?.iotaUsdPrice, totalStaked]);

  const { data: rawChartData = EMPTY_STAKING_CHART_DATA, isFetching } =
    useGetStakingChartDataQuery(range);

  const [actualRange, setActualRange] = useState<RangeSelectorType>(range);
  const [chartData, setChartData] = useState<IStakingChartData>([]);

  useEffect(() => {
    if (!isFetching && rawChartData.length > 0) {
      setChartData(rawChartData);
      setActualRange(range);
    }
  }, [rawChartData, isFetching, range]);

  return (
    <Card className={classes.root} wrapClassName={classes.wrapRoot}>
      {range === actualRange && (
        <StakingChart
          className={classes.chart}
          data={chartData}
          range={range}
        />
      )}

      <div className={classes.panel}>
        <Info
          label={t(keys.title)}
          subValue={
            totalStakedIOTA
              ? t(keys.unit.tokenValue, {
                  token: t(keys.tokens.native),
                  value: totalStakedIOTA.toFormat(3),
                })
              : ''
          }
          value={
            totalStaked
              ? t(keys.unit.$, {
                  value: totalStaked.toFormat(0, BigNumber.ROUND_DOWN, {
                    groupSeparator: ',',
                    groupSize: 3,
                    decimalSeparator: '.',
                  }),
                })
              : t(keys.common.none)
          }
        />

        <RangeSelector
          value={range}
          onChangeValue={newRange => setRange(newRange)}
        />
      </div>
    </Card>
  );
}
